var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { useEffect, useState } from "react";
import localize from "../../../core/utils/translation-files";
import { CloseOutlined, LoadingOutlined } from "@ant-design/icons";
import { ReportsApi } from "../../../core/api/reportsApi";
import { notifications } from "../notifications";
import { Select, Spin, Tooltip } from "antd";
import { usePromiseTracker } from "react-promise-tracker";
const Option = Select.Option;
const RecalculationTriggerDropdown = ({ groupOptionValues: { groupId, companyId, siteId, categoryId, subcategoryId, trackedItemId, }, onChangeGroupOptions, }) => {
    const [selectedTrackedItems, setSelectedTrackedItems] = useState([]);
    const [multiCategories, setMultiCategories] = useState([]);
    const [multiSubcategories, setMultiSubcategories] = useState([]);
    const [multiTrackedItems, setMultiTrackedItems] = useState([]);
    const { promiseInProgress: loadingCategories } = usePromiseTracker({
        area: "multiCategoriesLoader",
    });
    const { promiseInProgress: loadingSubcategories } = usePromiseTracker({
        area: "multiSubcategoriesLoader",
    });
    const { promiseInProgress: loadingTrackedItems } = usePromiseTracker({
        area: "multiTrackedItemsLoader",
    });
    useEffect(() => {
        const entityId = getSiteId(groupId, companyId, siteId);
        if (!entityId || entityId <= 0)
            return;
        ReportsApi.getTrackedItemsByEntity(entityId, -1, -1, {
            area: "multiCategoriesLoader",
        })
            .then((res) => {
            var _a;
            const categories = (_a = res === null || res === void 0 ? void 0 : res.data) !== null && _a !== void 0 ? _a : [];
            setMultiCategories(categories);
            setMultiSubcategories([]);
            setMultiTrackedItems([]);
        })
            .catch((err) => {
            notifications.error("Failed to load categories", err);
        });
    }, [groupId, companyId, siteId]);
    const handleChangeCategory = (value, _, selectedSite) => {
        var _a;
        const finalSiteId = selectedSite !== null && selectedSite !== void 0 ? selectedSite : siteId;
        const categoryOption = (_a = multiCategories.find((c) => c.categoryId === value)) !== null && _a !== void 0 ? _a : null;
        onChangeGroupOptions({
            groupId,
            companyId,
            siteId: finalSiteId,
            categoryId: value,
            subcategoryId: null,
            trackedItemId: null,
            categoryOption,
            subcategoryOption: null,
            // We pass the existing items so they aren't wiped from the filter state
            trackedItemOptions: selectedTrackedItems
                .map((item) => item.fullOption)
                .filter(Boolean),
        });
        const entityId = getSiteId(groupId, companyId, siteId);
        if (!entityId || entityId <= 0)
            return;
        ReportsApi.getTrackedItemsByEntity(entityId, value, -1, {
            area: "multiSubcategoriesLoader",
        })
            .then((res) => {
            var _a;
            const subcategories = (_a = res === null || res === void 0 ? void 0 : res.data) !== null && _a !== void 0 ? _a : [];
            setMultiSubcategories(subcategories);
            setMultiTrackedItems([]); // Clear dropdown list, but your selections stay in state
        })
            .catch((err) => {
            notifications.error("Failed to load subcategories", err);
        });
    };
    const handleChangeSubCategory = (value, _, selectedCategory) => {
        var _a;
        const finalCategoryId = selectedCategory !== null && selectedCategory !== void 0 ? selectedCategory : categoryId;
        const subcategoryOption = (_a = multiSubcategories.find((s) => s.subcategoryId === value)) !== null && _a !== void 0 ? _a : null;
        onChangeGroupOptions({
            groupId,
            companyId,
            siteId,
            categoryId: finalCategoryId,
            subcategoryId: value,
            trackedItemId: null,
            subcategoryOption,
            trackedItemOption: null,
        });
        if (!finalCategoryId)
            return;
        const entityId = getSiteId(groupId, companyId, siteId);
        if (!entityId || entityId <= 0)
            return;
        ReportsApi.getTrackedItemsByEntity(entityId, finalCategoryId, value, {
            area: "multiTrackedItemsLoader",
        })
            .then((res) => {
            var _a;
            const trackedItems = (_a = res === null || res === void 0 ? void 0 : res.data) !== null && _a !== void 0 ? _a : [];
            setMultiTrackedItems(trackedItems);
        })
            .catch((err) => {
            notifications.error("Failed to load tracked items", err);
        });
    };
    const handleChangeTrackedItem = (value, option, selectedSubcategory) => {
        if (!value || !option)
            return;
        if (selectedTrackedItems.some((item) => item.id === value))
            return;
        const { children, props, element, _owner } = option, restOfData = __rest(option, ["children", "props", "element", "_owner"]);
        const cleanOption = option.optiondata
            ? Object.assign({}, option.optiondata) : {
            reportId: option.reportId || value,
            name: option.name || "",
        };
        const newItem = {
            id: value,
            name: cleanOption.name,
            fullOption: cleanOption,
        };
        const updatedItems = [...selectedTrackedItems, newItem];
        setSelectedTrackedItems(updatedItems);
        onChangeGroupOptions({
            groupId,
            companyId,
            siteId,
            categoryId,
            subcategoryId: selectedSubcategory !== null && selectedSubcategory !== void 0 ? selectedSubcategory : subcategoryId,
            trackedItemId: value,
            trackedItemOptions: updatedItems
                .map((item) => item.fullOption)
                .filter(Boolean),
        });
    };
    const removeTrackedItem = (id) => {
        var _a, _b;
        const updatedItems = selectedTrackedItems.filter((item) => item.id !== id);
        setSelectedTrackedItems(updatedItems);
        onChangeGroupOptions({
            groupId,
            companyId,
            siteId,
            categoryId,
            subcategoryId,
            // Set the latest item as the "active" one, or null if empty
            trackedItemId: (_b = (_a = updatedItems[updatedItems.length - 1]) === null || _a === void 0 ? void 0 : _a.id) !== null && _b !== void 0 ? _b : null,
            trackedItemOptions: updatedItems
                .map((item) => item.fullOption)
                .filter(Boolean),
        });
    };
    function getSiteId(groupId, companyId, siteId) {
        return siteId > 0
            ? siteId
            : companyId > 0
                ? companyId
                : groupId > 0
                    ? groupId
                    : null;
    }
    const optionFilter = (input, option) => {
        var _a, _b, _c, _d, _e;
        return ((_e = (_d = (_c = (_b = (_a = option === null || option === void 0 ? void 0 : option.children) === null || _a === void 0 ? void 0 : _a.props) === null || _b === void 0 ? void 0 : _b.title) === null || _c === void 0 ? void 0 : _c.toString()) === null || _d === void 0 ? void 0 : _d.toLowerCase()) === null || _e === void 0 ? void 0 : _e.indexOf(input === null || input === void 0 ? void 0 : input.toLowerCase())) >= 0;
    };
    return (React.createElement("div", { className: "customFilterFields" },
        React.createElement("div", { className: "form-group col-12", "data-test": "category-dropdown" },
            React.createElement("div", { className: "ant-col ant-form-item-label" },
                React.createElement("label", null, localize(["TrackedItem.category", "name"]))),
            React.createElement(Select, { "data-test": "category-select", allowClear: true, showSearch: true, style: { padding: "0" }, className: "form-control", value: categoryId !== null && categoryId !== void 0 ? categoryId : undefined, placeholder: "Select Category", optionFilterProp: "children", filterOption: optionFilter, onChange: (value, option) => handleChangeCategory(value, option, siteId), key: "category-select", notFoundContent: loadingCategories ? (React.createElement("div", { style: { textAlign: "center", padding: "8px" } },
                    React.createElement(Spin, { indicator: React.createElement(LoadingOutlined, { spin: true }) }))) : (React.createElement("p", { style: { fontSize: "12px" } }, "No categories found!")) }, multiCategories === null || multiCategories === void 0 ? void 0 : multiCategories.map((option) => {
                var _a;
                return (React.createElement(Option, { key: option.categoryId, value: option.categoryId, optiondata: option, id: `option-div-container-${(_a = option === null || option === void 0 ? void 0 : option.name) === null || _a === void 0 ? void 0 : _a.toLowerCase().replaceAll(" ", "-")}` },
                    React.createElement(Tooltip, { placement: "top", title: option.name, overlayClassName: "dropdownTooltip" },
                        React.createElement("div", { style: {
                                width: "100%",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                            }, id: "option-div" }, option.name))));
            }))),
        React.createElement("div", { className: "form-group col-12", "data-test": "subcategory-dropdown" },
            React.createElement("div", { className: "ant-col ant-form-item-label" },
                React.createElement("label", null, localize(["TrackedItem.subCategory", "name"]))),
            React.createElement(Select, { "data-test": "subcategory-select", allowClear: true, disabled: !categoryId, showSearch: true, style: { padding: "0" }, className: "form-control", value: subcategoryId !== null && subcategoryId !== void 0 ? subcategoryId : undefined, placeholder: "Select Subcategory", optionFilterProp: "children", filterOption: optionFilter, onChange: (value, option) => handleChangeSubCategory(value, option, categoryId), key: "subcategory-select", notFoundContent: loadingSubcategories ? (React.createElement("div", { style: { textAlign: "center", padding: "8px" } },
                    React.createElement(Spin, { indicator: React.createElement(LoadingOutlined, { spin: true }) }))) : (React.createElement("p", { style: { fontSize: "12px" } }, "No subcategories found!")) }, multiSubcategories === null || multiSubcategories === void 0 ? void 0 : multiSubcategories.map((option) => {
                var _a;
                return (React.createElement(Option, { key: option.subcategoryId, value: option.subcategoryId, optiondata: option, id: `option-div-container-${(_a = option === null || option === void 0 ? void 0 : option.name) === null || _a === void 0 ? void 0 : _a.toLowerCase().replaceAll(" ", "-")}` },
                    React.createElement(Tooltip, { placement: "top", title: option.name, overlayClassName: "dropdownTooltip" },
                        React.createElement("div", { style: {
                                width: "100%",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                            }, id: "option-div" }, option.name))));
            }))),
        React.createElement("div", { className: "form-group col-12", "data-test": "trackedItem-dropdown" },
            React.createElement("div", { className: "ant-col ant-form-item-label" },
                React.createElement("label", null, localize(["TrackedItem.trackedItem", "name"]))),
            React.createElement(Select, { "data-test": "trackeditem-select", allowClear: true, disabled: !subcategoryId, showSearch: true, style: { padding: "0" }, className: "form-control", value: trackedItemId !== null && trackedItemId !== void 0 ? trackedItemId : undefined, placeholder: "Select Tracked Item", optionFilterProp: "children", filterOption: optionFilter, onChange: (value, option) => handleChangeTrackedItem(value, option, subcategoryId), key: "trackeditem-select", notFoundContent: loadingTrackedItems ? (React.createElement("div", { style: { textAlign: "center", padding: "8px" } },
                    React.createElement(Spin, { indicator: React.createElement(LoadingOutlined, { spin: true }) }))) : (React.createElement("p", { style: { fontSize: "12px" } }, "No tracked items found!")) }, multiTrackedItems === null || multiTrackedItems === void 0 ? void 0 : multiTrackedItems.map((option) => {
                var _a;
                return (React.createElement(Option, { key: option.reportId, value: option.reportId, optiondata: option, name: option.name, id: `option-div-container-${(_a = option === null || option === void 0 ? void 0 : option.name) === null || _a === void 0 ? void 0 : _a.toLowerCase().replaceAll(" ", "-")}` },
                    React.createElement(Tooltip, { placement: "top", title: option.name, overlayClassName: "dropdownTooltip" },
                        React.createElement("div", { style: {
                                width: "100%",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                            }, id: "option-div" }, option.name))));
            }))),
        React.createElement("div", { className: "form-group col-12 mt-2" },
            React.createElement("div", { className: "ant-col ant-form-item-label" },
                React.createElement("label", null, "Selected Tracked Items")),
            React.createElement("div", { style: {
                    minHeight: "32px",
                    maxHeight: "120px",
                    overflowY: "auto",
                    border: "1px solid #d9d9d9",
                    borderRadius: "6px",
                    padding: "6px 10px",
                    display: "flex",
                    flexWrap: "wrap",
                    gap: "8px",
                    background: "#fff",
                    scrollbarWidth: "thin",
                    scrollbarColor: "#d9d9d9 transparent",
                } }, selectedTrackedItems &&
                selectedTrackedItems.map((item) => (React.createElement("div", { key: item.id, style: {
                        background: "#f5f5f5",
                        padding: "3px 10px",
                        borderRadius: "16px",
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        fontSize: "13px",
                    } },
                    React.createElement("span", { style: { fontSize: "12px" } }, item.name),
                    React.createElement(CloseOutlined, { style: { cursor: "pointer", fontSize: "9px" }, onClick: () => removeTrackedItem(item.id) }))))))));
};
export default React.memo(RecalculationTriggerDropdown);
